import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Input, Pagination, Select, Skeleton } from 'antd';
import { get } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCar,
  faCircleExclamation,
  faHotel,
  faMagnifyingGlass,
  faPersonHiking,
  faSuitcase,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import { IReservation } from '@share/common-types';
import { ReservationsAPI } from '@share/api';
import { getStatic, IReservationsState } from '@share/store/slices';
import { FunctionalRootState, RootState } from '@share/utils';
import { IInsuranceState } from '@store/slices';

import { ReservationAction } from '../../../reservations/reservation-actions';
import { parseApiDate } from '../../utils';
import { AccountEmptyState } from '../empty-state';

import styles from './styles.module.scss';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

type StatusKey = 'active' | 'past' | 'cancelled' | 'all';
type ProductKey = '' | 'Hotel' | 'Condo' | 'Car' | 'Activity';
type SortKey = 'createdAt' | 'checkIn';

const STATUSES: { key: StatusKey; labelId: string }[] = [
  { key: 'active', labelId: 'my.account.hub.bookings.status.active' },
  { key: 'past', labelId: 'my.account.hub.bookings.status.past' },
  { key: 'cancelled', labelId: 'my.account.hub.bookings.status.cancelled' },
  { key: 'all', labelId: 'my.account.hub.bookings.status.all' },
];

// Values as the reservations API expects them (capitalised), see the legacy bookings column filter.
const PRODUCTS: { key: ProductKey; labelId: string }[] = [
  { key: '', labelId: 'my.account.hub.bookings.product.all' },
  { key: 'Hotel', labelId: 'my.account.hub.bookings.product.hotels' },
  { key: 'Condo', labelId: 'my.account.hub.bookings.product.condos' },
  { key: 'Car', labelId: 'my.account.hub.bookings.product.cars' },
  { key: 'Activity', labelId: 'my.account.hub.bookings.product.activities' },
];

const PRODUCT_ICONS: Record<string, IconDefinition> = {
  hotel: faHotel,
  condo: faBuilding,
  car: faCar,
  activity: faPersonHiking,
};

const getProduct = (booking: IReservation): string => (booking.bookingType || '').toLowerCase();
const getKey = (booking: IReservation): string => booking.bookingId || String(booking.bookingInternalId);

const getInlineImage = (booking: IReservation): string | null => {
  const product = getProduct(booking);
  if (product === 'car') {
    return get(booking, 'bookingUserCar.vehicle.pictureURL', null);
  }
  if (product === 'activity') {
    return get(booking, 'bookingUserActivity.titleImage', null);
  }
  return null;
};

const hasAmount = (value: string | null | undefined): boolean => Number((value || '').replace(/[^0-9.]/g, '')) > 0;

interface IListState {
  loading: boolean;
  failed: boolean;
  items: IReservation[];
  total: number;
}

/** Bookings as cards: status tabs, product filter, search, sort; details open the existing booking modal. */
export const AccountBookings = (): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();

  const reservationsStore: IReservationsState = useSelector((state: FunctionalRootState) => state.reservationsStore);
  const insuranceStore: IInsuranceState = useSelector((state: FunctionalRootState) => state.insuranceStore);

  const [status, setStatus] = useState<StatusKey>('active');
  const [product, setProduct] = useState<ProductKey>('');
  const [sort, setSort] = useState<SortKey>('createdAt');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [list, setList] = useState<IListState>({ loading: true, failed: false, items: [], total: 0 });
  const [images, setImages] = useState<Record<string, string | null>>({});
  const requestedImages = useRef<Set<string>>(new Set());
  const [selected, setSelected] = useState<IReservation | null>(null);

  // Debounce the text search so each keystroke doesn't hit the API.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let isActive = true;
    setList(current => ({ ...current, loading: true, failed: false }));

    ReservationsAPI.getReservations({
      bookingId: 0,
      propertyName: search,
      leadName: '',
      bookingType: product,
      status,
      page,
      pageSize: PAGE_SIZE,
      orderBy: sort,
      sortOrder: 'desc',
    })
      .then(({ data }) => {
        if (!isActive) {
          return;
        }
        if (data?.status) {
          setList({ loading: false, failed: true, items: [], total: 0 });
          return;
        }
        setList({ loading: false, failed: false, items: data?.items || [], total: data?.totalResults || 0 });
      })
      .catch((error: unknown) => {
        console.error('Account hub: bookings request failed', error);
        if (isActive) {
          setList({ loading: false, failed: true, items: [], total: 0 });
        }
      });

    return () => {
      isActive = false;
    };
  }, [status, product, sort, search, page, reloadKey]);

  // Photos: cars and activities carry one; hotels and condos load theirs from the property content, once each.
  useEffect(() => {
    list.items.forEach(booking => {
      const key = getKey(booking);
      if (requestedImages.current.has(key)) {
        return;
      }
      requestedImages.current.add(key);

      const inline = getInlineImage(booking);
      if (inline || !booking.propertyId) {
        setImages(current => ({ ...current, [key]: inline }));
        return;
      }
      ReservationsAPI.getStatic(booking.propertyId)
        .then(({ data }) => setImages(current => ({ ...current, [key]: data?.imageUrl || null })))
        .catch((error: unknown) => {
          console.error('Account hub: booking image request failed', error);
          setImages(current => ({ ...current, [key]: null }));
        });
    });
  }, [list.items]);

  const formatDate = (value: string | null | undefined): string => {
    const date = parseApiDate(value);
    return date ? intl.formatDate(date, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  };

  const handleOpen = (booking: IReservation) => {
    // The details modal reads the property content (address, country) from the store, like the legacy table.
    if (getProduct(booking) !== 'car' && booking.propertyId) {
      dispatch(getStatic(booking.propertyId));
    }
    setSelected(booking);
  };

  const resetTo = (setter: () => void) => {
    setter();
    setPage(1);
  };

  const isFiltered = !!product || !!search;

  const renderList = () => {
    if (list.loading) {
      return (
        <ul className={styles.list}>
          {[0, 1, 2].map(key => (
            <li key={key} className={styles.card}>
              <div className={styles.media} />
              <div className={styles.body}><Skeleton active paragraph={{ rows: 3 }} /></div>
            </li>
          ))}
        </ul>
      );
    }
    if (list.failed) {
      return (
        <AccountEmptyState
          variant="panel"
          icon={faCircleExclamation}
          title={intl.formatMessage({ id: 'my.account.hub.bookings.error_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.bookings.error_body' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.bookings.retry' }), onClick: () => setReloadKey(key => key + 1) }}
        />
      );
    }
    if (!list.items.length) {
      return isFiltered ? (
        <AccountEmptyState
          variant="panel"
          icon={faMagnifyingGlass}
          title={intl.formatMessage({ id: 'my.account.hub.bookings.no_match_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.bookings.no_match_body' })}
          action={{
            label: intl.formatMessage({ id: 'my.account.hub.bookings.clear_filters' }),
            onClick: () => resetTo(() => {
              setProduct('');
              setSearchInput('');
              setSearch('');
            }),
          }}
        />
      ) : (
        <AccountEmptyState
          variant="panel"
          icon={faSuitcase}
          title={intl.formatMessage({ id: 'my.account.hub.bookings.empty_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.bookings.empty_body' })}
        />
      );
    }

    return (
      <ul className={styles.list}>
        {list.items.map(booking => {
          const key = getKey(booking);
          const productKey = getProduct(booking);
          const isCar = productKey === 'car';
          const image = images[key];
          const totalPaid = booking.priceDetail?.totalPaid;
          const cashback = booking.priceDetail?.cashbackEarned;
          const confirmation = booking.supplierConfirmation || booking.bookingId;
          const subtitle = [booking.propertyCity, confirmation ? intl.formatMessage({ id: 'my.account.hub.bookings.confirmation' }, { code: confirmation }) : '']
            .filter(Boolean)
            .join(' · ');

          return (
            <li key={key} className={styles.card}>
              <div className={styles.media}>
                {image ? (
                  <img src={image} alt="" loading="lazy" className={styles.image} />
                ) : (
                  <div className={styles.placeholder} aria-hidden="true">
                    <FontAwesomeIcon icon={PRODUCT_ICONS[productKey] || faSuitcase} />
                  </div>
                )}
                {booking.bookingType ? <span className={styles.type}>{booking.bookingType}</span> : null}
              </div>

              <div className={styles.body}>
                <div className={styles.titleRow}>
                  <div className={styles.titleBlock}>
                    <h3 className={styles.name}>{booking.propertyName}</h3>
                    {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
                  </div>
                  <div className={styles.badges}>
                    {booking.bookingStatusEnum ? (
                      <span className={`${styles.badge} ${styles[`badge_${String(booking.bookingStatusEnum).toLowerCase()}`] || ''}`}>
                        {booking.bookingStatusEnum}
                      </span>
                    ) : null}
                    {typeof booking.refundable === 'boolean' ? (
                      <span className={`${styles.badge} ${booking.refundable ? styles.badge_confirmed : ''}`}>
                        {intl.formatMessage({ id: booking.refundable ? 'refundable' : 'non.refundable' })}
                      </span>
                    ) : null}
                  </div>
                </div>

                <dl className={styles.facts}>
                  <div>
                    <dt>{intl.formatMessage({ id: isCar ? 'cars.pickup' : 'my.account.hub.next_trip.check_in' })}</dt>
                    <dd>{formatDate(booking.checkIn)}</dd>
                  </div>
                  <div>
                    <dt>{intl.formatMessage({ id: isCar ? 'cars.dropoff' : 'check.out' })}</dt>
                    <dd>{formatDate(booking.checkOut)}</dd>
                  </div>
                  {!isCar && booking.numberGuest ? (
                    <div>
                      <dt>{intl.formatMessage({ id: 'my.account.hub.next_trip.guests' })}</dt>
                      <dd>
                        {intl.formatMessage(
                          { id: 'my.account.hub.next_trip.guests_value' },
                          { guests: booking.numberGuest, rooms: booking.roomsCount || 1 },
                        )}
                      </dd>
                    </div>
                  ) : null}
                  {booking.leadName ? (
                    <div>
                      <dt>{intl.formatMessage({ id: 'my.account.hub.bookings.lead' })}</dt>
                      <dd>{booking.leadName}</dd>
                    </div>
                  ) : null}
                </dl>

                <div className={styles.footer}>
                  <button type="button" className={styles.primaryButton} onClick={() => handleOpen(booking)}>
                    {intl.formatMessage({ id: 'my.account.hub.bookings.view_details' })}
                  </button>
                  <div className={styles.price}>
                    {hasAmount(cashback) ? (
                      <span className={styles.earns}>{intl.formatMessage({ id: 'my.account.hub.next_trip.earns' }, { amount: cashback })}</span>
                    ) : null}
                    {hasAmount(totalPaid) ? <span className={styles.total}>{totalPaid}</span> : null}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={styles.bookings}>
      <div className={styles.toolbar}>
        <div role="tablist" aria-label={intl.formatMessage({ id: 'my.account.hub.bookings.status_label' })} className={styles.segmented}>
          {STATUSES.map(item => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={status === item.key}
              className={status === item.key ? `${styles.segment} ${styles.segmentActive}` : styles.segment}
              onClick={() => resetTo(() => setStatus(item.key))}
            >
              {intl.formatMessage({ id: item.labelId })}
            </button>
          ))}
        </div>
        <div className={styles.controls}>
          <div className={styles.searchWrap}>
            <Input
              allowClear
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />}
              placeholder={intl.formatMessage({ id: 'my.account.hub.bookings.search' })}
              aria-label={intl.formatMessage({ id: 'my.account.hub.bookings.search' })}
            />
          </div>
          <Select<SortKey>
            value={sort}
            onChange={value => resetTo(() => setSort(value))}
            className={styles.sort}
            dropdownMatchSelectWidth={false}
            aria-label={intl.formatMessage({ id: 'my.account.hub.bookings.sort' })}
            options={[
              { value: 'createdAt', label: intl.formatMessage({ id: 'my.account.hub.bookings.sort_newest' }) },
              { value: 'checkIn', label: intl.formatMessage({ id: 'my.account.hub.bookings.sort_checkin' }) },
            ]}
          />
        </div>
      </div>

      <div className={styles.products} role="group" aria-label={intl.formatMessage({ id: 'my.account.hub.bookings.product_label' })}>
        <span className={styles.productsLabel}>{intl.formatMessage({ id: 'my.account.hub.bookings.product_label' })}</span>
        {PRODUCTS.map(item => (
          <button
            key={item.key || 'all'}
            type="button"
            aria-pressed={product === item.key}
            className={product === item.key ? `${styles.pill} ${styles.pillActive}` : styles.pill}
            onClick={() => resetTo(() => setProduct(item.key))}
          >
            {intl.formatMessage({ id: item.labelId })}
          </button>
        ))}
      </div>

      {!list.loading && !list.failed && list.total > 0 ? (
        <p className={styles.count}>{intl.formatMessage({ id: 'my.account.hub.bookings.count' }, { count: list.total })}</p>
      ) : null}

      {renderList()}

      {list.total > PAGE_SIZE ? (
        <Pagination
          className={styles.pagination}
          current={page}
          pageSize={PAGE_SIZE}
          total={list.total}
          showSizeChanger={false}
          onChange={value => {
            setPage(value);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      ) : null}

      <ReservationAction
        loading={reservationsStore?.loading || insuranceStore?.loading}
        error={insuranceStore?.error}
        reservation={selected}
        staticData={reservationsStore?.result?.staticData || null}
        visible={!!selected}
        onClose={() => setSelected(null)}
        onRefresh={() => setReloadKey(key => key + 1)}
      />
    </div>
  );
};
